<template>
	<div class="empty-state" v-if="!messages.length && !loading">
		{{ $t('groupsettings_mediatray_files_none_yet') }}
	</div>

	<ScrollToLoad
		class="group-media-files"
		:loading="loading"
		@bottom="loadMoreMessages"
	>
		<MediaItem
			v-for="file in visibleItems"
			:item="file"
			:key="file.id"
			:messageId="file.message_id"
		>
			<MediaFile :media="file" v-if="file.isFile" />
			<AudioPlayer
				v-if="file.isAudio"
				:src="file.mediaUrl"
				:duration="file.duration"
			/>
		</MediaItem>

		<template #spinner>
			<IosSpinner
				v-if="loading"
				top="47%"
				left="0"
				right="0"
				style="margin-left: auto; margin-right: auto"
			>
				<template #title>{{ $t('common_loader_loading_title') }}</template>
			</IosSpinner>
		</template>
	</ScrollToLoad>
</template>

<script setup>
import { onMounted, computed, ref } from 'vue'
import { getCancelToken } from '@/helpers/api'
import { fetchHistoryWithoutStore } from '@/api/chat'
import { normalizeMessage } from '@/store/messages'
import MediaItem from '@/components/GroupSettings/MediaItem'
import MediaFile from '@/components/MediaView/MediaFile'
import AudioPlayer from '@/components/MediaView/AudioPlayer'
import { mediaTypes } from '@/helpers/constants'
import { ScrollToLoad, useAlert } from '@/components/common'
import IosSpinner from '@/components/IosSpinner'

const VISIBLE_PAGE_SIZE = 24

const props = defineProps({ chatId: [String, Number] })

const alert = useAlert()

const messages = ref([])
const loading = ref(false)
const currentPage = ref(0)

let latest = null
let cancelToken = null
let everythingLoaded = false

const mediaItems = computed(() =>
	messages.value
		.map(({ id, user, attachments, meeting }) => {
			return attachments
				.filter((m) => m.isFile || m.isAudio)
				.map((media) => ({
					...media,
					messageId: id,
					user,
					meeting,
				}))
		})
		.flat()
)

const visibleCount = computed(() => VISIBLE_PAGE_SIZE * currentPage.value)
const visibleItems = computed(() =>
	mediaItems.value.slice(0, visibleCount.value)
)

onMounted(() => {
	getMessageHistory()
})

function loadMoreMessages(isAtBottom) {
	if (!isAtBottom || !messages.value.length) return false
	getMessageHistory()
}

async function getMessageHistory() {
	if (loading.value || everythingLoaded) return
	try {
		loading.value = true

		if (mediaItems.value.length > visibleCount.value) {
			// we already have more items loaded, just need to display them
			loading.value = false
			currentPage.value += 1
			return
		}

		const body = {
			bubble_id: props.chatId,
			mediatypes: [mediaTypes.FILE, mediaTypes.AUDIO].join(','),
		}
		if (latest) {
			body.latest = latest
		}

		if (cancelToken) {
			cancelToken.cancel()
		}

		cancelToken = getCancelToken()

		const data = await fetchHistoryWithoutStore(body, cancelToken)

		if (data.messages.length == 0) {
			everythingLoaded = true
			loading.value = false
			return
		}

		messages.value = messages.value.concat(data.messages.map(normalizeMessage))
		currentPage.value += 1

		if (data.pagesize === data.messages.length) {
			latest = data.messages.at(-1).id
		} else {
			everythingLoaded = true
		}

		loading.value = false
	} catch (e) {
		console.error(e)
		alert.error(e)
	}
}
</script>

<style lang="scss" scoped>
.group-media-files {
	max-height: calc(100vh - 285px);
	padding-bottom: 40px;
	overflow-y: auto;

	:deep(.view-in-chat) {
		.left {
			flex: 1.2;
		}
	}

	.media-file-container {
		border: 0;
		max-width: 100%;
	}

	:deep(.audio-player-container) {
		border: 0;
		flex-grow: 1;
		max-width: unset;
		height: unset;
	}
}

.empty-state {
	position: absolute;
	top: 46%;
	left: calc(46% - 125px);
	font-family: 'Avenir-Medium';
	font-size: 28px;
	color: #4f5053;
	text-align: center;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.group-media-files {
		.ios-spinner {
			background: var(--neutral-100) !important;
		}
	}
}
</style>
